import clsx from 'clsx';

//hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

import PropTypes from 'prop-types';

import { DEFAULT_POST_CONFIG } from '@/lib/posts/defaultPostConfig';
import { getPostType, POST, TW_POST_TYPES } from '@/lib/posts/postTypes';
//lib
import { TWITTER } from '@/lib/socialProfile';

//components
import Post from '@/components/base/Post';
import HoverLabelAndAlbums from '@/components/base/Post/HoverLabelAndAlbums';
import { QuoteBadge } from '@/components/composite/Post/Twitter/QuoteBadge';
import { ReplyBadge } from '@/components/composite/Post/Twitter/ReplyBadge';
import { RetweetBadge } from '@/components/composite/Post/Twitter/RetweetBadge';

const TwitterPost = ({
    data,
    isLoading,
    config,
    openViewer,
    isExport = false,
    remoteMedia = false,
    handleLabelsEvent,
    handleAlbumsEvent,
    postConfig = { hasHover: true, hasFooter: true, showLabels: true, showAlbums: true },
    showHeader = true
}) => {
    const { OffsetReplace } = useTransformStringOnOffset();
    const type = getPostType({ network: TWITTER, type: data?.sub_type });
    const mergedPostConfig = { ...DEFAULT_POST_CONFIG, ...postConfig };

    if (isLoading) {
        return <Post.Placeholder postType={POST} isExport={isExport} />;
    }

    // return initial tweet formatted caption when retweet
    const caption =
        data?.initial_tweet && data?.type === TW_POST_TYPES.TYPE_RETWEET
            ? data?.initial_tweet?.caption_formatted
            : data?.caption_formatted;

    // return initial tags when retweet
    const tags =
        data?.initial_tweet && data?.type === TW_POST_TYPES.TYPE_RETWEET
            ? data?.initial_tweet?.tags
            : data?.tags;

    // is a reply
    const isReply = data?.initial_tweet !== null;

    return (
        <div className="w-full">
            {showHeader && <Post.Header data={data} isExport={isExport} />}
            <Post
                data={data}
                config={config}
                isLoading={isLoading}
                isExport={isExport}
                className={clsx('w-full bg-white', {
                    'p-0.5': !mergedPostConfig?.hasFooter,
                    'px-2 pt-2': mergedPostConfig?.hasFooter,
                    'dark:bg-icoDarkMode-anthracite': !isExport
                })}>
                {({ referenceElement }) => (
                    <>
                        <Post.Content className="w-full">
                            {mergedPostConfig?.hasHover ? (
                                <HoverLabelAndAlbums
                                    openViewer={openViewer}
                                    referenceElement={referenceElement}
                                    handleLabelsEvent={handleLabelsEvent}
                                    handleAlbumsEvent={handleAlbumsEvent}
                                    showLabels={mergedPostConfig?.showLabels}
                                    showAlbums={mergedPostConfig?.showAlbums && !isReply}
                                    socialProfile={data?.social_profile}
                                />
                            ) : null}
                            {type && type !== TW_POST_TYPES.TYPE_STATUS ? (
                                <Post.PostTypeIcon
                                    type={type === TW_POST_TYPES.TYPE_PHOTO ? 'content' : type}
                                />
                            ) : null}
                            {data && data?.promoted_post ? <Post.Promoted /> : null}

                            <Post.Status
                                remoteMedia={remoteMedia}
                                withMedia={
                                    type !== TW_POST_TYPES.TYPE_STATUS &&
                                    type !== TW_POST_TYPES.TYPE_LINK &&
                                    type !== TW_POST_TYPES.TYPE_POLL
                                }
                                formattedCaption={
                                    <>
                                        {data?.initial_author &&
                                        (data?.type === TW_POST_TYPES.TYPE_RETWEET ||
                                            data?.type === TW_POST_TYPES.TYPE_QUOTE ||
                                            data?.type === TW_POST_TYPES.TYPE_REPLY) ? (
                                            <div className="mb-5 flex flex-wrap items-center space-x-1 text-xs">
                                                {data?.type === TW_POST_TYPES.TYPE_RETWEET ? (
                                                    <RetweetBadge isExport={isExport} />
                                                ) : null}
                                                {data?.type === TW_POST_TYPES.TYPE_QUOTE ? (
                                                    <QuoteBadge isExport={isExport} />
                                                ) : null}
                                                {data?.type === TW_POST_TYPES.TYPE_REPLY ? (
                                                    <ReplyBadge isExport={isExport} />
                                                ) : null}
                                                {data?.initial_author?.tag ? (
                                                    <a
                                                        href={data?.initial_author?.permalink}
                                                        className="text-icoBlue">
                                                        {data?.initial_author?.tag}
                                                    </a>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        <span className="break-words">
                                            <OffsetReplace
                                                text={caption}
                                                components={Object.assign(
                                                    {
                                                        a: <span className="text-icoBlue" />,
                                                        br: <br />
                                                    },
                                                    tags?.map((tag, id) => (
                                                        // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                        <a
                                                            key={id}
                                                            href={tag.link}
                                                            className="inline-block text-icoBlue"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        />
                                                    ))
                                                )}
                                            />
                                        </span>
                                        {type === TW_POST_TYPES.TYPE_POLL &&
                                        data?.medias[0]?.poll?.options ? (
                                            <div className="mt-5 flex flex-col space-y-5 text-xs">
                                                {data?.medias[0]?.poll?.options?.map(
                                                    (option, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex items-center justify-between">
                                                            <span>{option?.label}</span>
                                                            {(option?.percentage ||
                                                                option?.percentage === 0) && (
                                                                <span>{option?.percentage}%</span>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        ) : null}
                                    </>
                                }
                            />
                        </Post.Content>
                        {mergedPostConfig?.hasFooter ? <Post.Footer type={type} /> : null}
                    </>
                )}
            </Post>
        </div>
    );
};

TwitterPost.propTypes = {
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    config: PropTypes.object,
    openViewer: PropTypes.func,
    addLabel: PropTypes.func,
    addAlbum: PropTypes.func
};

export default TwitterPost;
